import { useMemo, useState, type FormEvent, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Client, RevenueEntry } from "../lib/types";
import { money, longDate } from "../lib/format";
import {
  PageHeader,
  Card,
  Loading,
  ErrorNote,
  EmptyState,
  Modal,
  Spinner,
  TableScroll,
} from "../components/ui";
import { IconTrash, IconEdit } from "../components/icons";
import { SegmentedControl } from "../components/SegmentedControl";

export function Revenue() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const clientId = params.get("client") ?? "";
  const [editing, setEditing] = useState<RevenueEntry | null>(null);
  const [view, setView] = useState<"grid" | "entries">("grid");

  const clients = useQuery({
    queryKey: ["clients", false],
    queryFn: () => api.get<{ clients: Client[] }>("/api/clients?archived=false"),
  });

  const entries = useQuery({
    queryKey: ["revenue", clientId],
    queryFn: () => api.get<{ entries: RevenueEntry[] }>(`/api/clients/${clientId}/revenue`),
    enabled: Boolean(clientId),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/api/revenue/${id}`),
    onSuccess: () => invalidate(),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["revenue", clientId] });
    qc.invalidateQueries({ queryKey: ["clientDetail", clientId] });
    qc.invalidateQueries({ queryKey: ["overview"] });
  }

  const selectedClient = clients.data?.clients.find((c) => c.id === clientId);

  return (
    <div>
      <PageHeader
        title="Revenue"
        subtitle="Log store sales (MUR) — the manual layer that powers true ROAS. Meta can't see store revenue without a purchase pixel."
      />

      <Card className="mb-6">
        <label className="label">Client</label>
        <select
          className="input max-w-sm"
          value={clientId}
          onChange={(e) => setParams(e.target.value ? { client: e.target.value } : {})}
        >
          <option value="">— select a client —</option>
          {clients.data?.clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </Card>

      {!clientId ? (
        <EmptyState title="Pick a client" hint="Choose a client above to log and view revenue." />
      ) : (
        <>
          <SegmentedControl
            className="mb-4"
            size="md"
            ariaLabel="Revenue view"
            options={[
              { value: "grid", label: "Daily grid" },
              { value: "entries", label: "Entries" },
            ]}
            value={view}
            onChange={setView}
          />
          {view === "grid" ? (
            <MonthlyRevenueGrid clientId={clientId} onSaved={invalidate} />
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
              <EntryForm clientId={clientId} onSaved={invalidate} />

          <div>
            <h2 className="mb-3 text-sm font-semibold text-ink">
              History{selectedClient ? ` — ${selectedClient.name}` : ""}
            </h2>
            {entries.isLoading ? (
              <Loading />
            ) : entries.error ? (
              <ErrorNote message={(entries.error as Error).message} />
            ) : (entries.data?.entries.length ?? 0) === 0 ? (
              <EmptyState title="No revenue logged yet" />
            ) : (
              <div className="card overflow-hidden">
                <TableScroll>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-muted">
                      <th className="px-4 py-3 font-medium">Period</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 text-right font-medium">Amount</th>
                      <th className="px-4 py-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.data!.entries.map((r) => (
                      <tr key={r.id} className="border-b border-hairline/60 last:border-0 hover:bg-surface-2">
                        <td className="px-4 py-3 tnum">
                          {longDate(r.period_start)}
                          {r.period_end !== r.period_start && ` – ${longDate(r.period_end)}`}
                          {r.note && <div className="text-xs text-muted">{r.note}</div>}
                        </td>
                        <td className="px-4 py-3 capitalize text-ink-secondary">{r.period_type}</td>
                        <td className="px-4 py-3 text-right tnum font-medium">{money(r.amount_mur)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button className="btn-ghost px-2 py-1" onClick={() => setEditing(r)} title="Edit">
                              <IconEdit width={15} height={15} />
                            </button>
                            <button
                              className="btn-danger px-2 py-1"
                              onClick={() => confirm("Delete this revenue entry?") && del.mutate(r.id)}
                              title="Delete"
                            >
                              <IconTrash width={15} height={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </TableScroll>
              </div>
            )}
          </div>
            </div>
          )}
        </>
      )}

      {editing && (
        <Modal open onClose={() => setEditing(null)} title="Edit revenue entry">
          <EntryForm
            clientId={clientId}
            entry={editing}
            onSaved={() => {
              setEditing(null);
              invalidate();
            }}
          />
        </Modal>
      )}
    </div>
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function weekday(iso: string): string {
  return WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()] ?? "";
}
function daysOfMonth(month: string): string[] {
  const { start, end } = monthBounds(month);
  const out: string[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) out.push(d);
  return out;
}

/**
 * Fill in a whole month's DAILY revenue at once. Daily figures give exact ROAS
 * at any range (no proration), which is why this is the primary entry mode.
 */
function MonthlyRevenueGrid({ clientId, onSaved }: { clientId: string; onSaved: () => void }) {
  const qc = useQueryClient();
  const thisMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const [month, setMonth] = useState(thisMonth);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const entries = useQuery({
    queryKey: ["revenue", clientId],
    queryFn: () => api.get<{ entries: RevenueEntry[] }>(`/api/clients/${clientId}/revenue`),
    enabled: Boolean(clientId),
  });

  // Prefill the grid from existing daily entries for the selected month.
  useEffect(() => {
    const map: Record<string, string> = {};
    for (const e of entries.data?.entries ?? []) {
      if (e.period_type === "daily" && e.period_start.startsWith(`${month}-`)) {
        map[e.period_start] = String(e.amount_mur);
      }
    }
    setValues(map);
  }, [entries.data, month]);

  const days = useMemo(() => daysOfMonth(month), [month]);
  const total = useMemo(
    () => days.reduce((s, d) => s + (Number(values[d]) || 0), 0),
    [days, values]
  );

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/clients/${clientId}/revenue/daily`, {
        month,
        days: days.map((d) => ({
          date: d,
          amount: values[d] === undefined || values[d] === "" ? null : Number(values[d]),
        })),
      }),
    onSuccess: () => {
      setSaved(true);
      onSaved();
      qc.invalidateQueries({ queryKey: ["revenue", clientId] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <label className="label">Month</label>
          <input
            type="month"
            className="input max-w-[12rem]"
            value={month}
            onChange={(e) => setMonth(e.target.value || thisMonth)}
          />
        </div>
        <div className="text-right">
          <div className="text-xs text-muted">Month total</div>
          <div className="text-lg font-semibold tnum text-ink">{money(total)}</div>
        </div>
      </div>

      {entries.isLoading ? (
        <Loading />
      ) : (
        <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-hairline">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface">
              <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-3 py-2 font-medium">Day</th>
                <th className="px-3 py-2 text-right font-medium">Revenue (Rs)</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => {
                const wd = weekday(d);
                const weekend = wd === "Sat" || wd === "Sun";
                return (
                  <tr
                    key={d}
                    className={`border-b border-hairline/60 last:border-0 ${weekend ? "bg-surface-2/40" : ""}`}
                  >
                    <td className="px-3 py-1.5 tnum text-ink-secondary">
                      {d.slice(8)} <span className="text-muted">{wd}</span>
                    </td>
                    <td className="px-3 py-1 text-right">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="—"
                        className="input tnum w-32 py-1 text-right"
                        value={values[d] ?? ""}
                        onChange={(e) => setValues((v) => ({ ...v, [d]: e.target.value }))}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {err && (
        <div className="mt-3">
          <ErrorNote message={err} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          className="btn-primary"
          onClick={() => {
            setErr(null);
            save.mutate();
          }}
          disabled={save.isPending}
        >
          {save.isPending && <Spinner className="h-4 w-4" />} Save month
        </button>
        {saved && <span className="text-xs text-good">Saved</span>}
        <span className="text-xs text-muted">Empty days are cleared. Daily figures give exact ROAS.</span>
      </div>
    </Card>
  );
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** First and last day (YYYY-MM-DD) of a "YYYY-MM" month. */
function monthBounds(yyyymm: string): { start: string; end: string } {
  const [y, m] = yyyymm.split("-").map(Number); // m is 1-12
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate(); // day 0 of next month
  return { start: `${yyyymm}-01`, end: `${yyyymm}-${String(lastDay).padStart(2, "0")}` };
}

type PeriodType = "daily" | "weekly" | "monthly";

function EntryForm({
  clientId,
  entry,
  onSaved,
}: {
  clientId: string;
  entry?: RevenueEntry;
  onSaved: () => void;
}) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [periodType, setPeriodType] = useState<PeriodType>(entry?.period_type ?? "daily");
  const [start, setStart] = useState(entry?.period_start ?? today);
  const [end, setEnd] = useState(entry?.period_end ?? today);
  const [amount, setAmount] = useState(entry ? String(entry.amount_mur) : "");
  const [note, setNote] = useState(entry?.note ?? "");
  const [err, setErr] = useState<string | null>(null);

  // Keep end sensible when switching modes / changing start.
  useEffect(() => {
    if (periodType === "daily") {
      setEnd(start);
    } else if (periodType === "monthly") {
      const b = monthBounds(start.slice(0, 7));
      setStart(b.start);
      setEnd(b.end);
    } else if (end < start) {
      setEnd(addDays(start, 6));
    }
  }, [periodType, start]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        period_type: periodType,
        period_start: start,
        period_end: periodType === "daily" ? start : end,
        amount_mur: Number(amount),
        note: note || null,
      };
      return entry
        ? api.patch(`/api/revenue/${entry.id}`, payload)
        : api.post(`/api/clients/${clientId}/revenue`, payload);
    },
    onSuccess: onSaved,
    onError: (e) => setErr((e as Error).message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!amount || Number(amount) < 0) {
      setErr("Enter a valid amount.");
      return;
    }
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="card space-y-4 p-5">
      {!entry && <h2 className="text-sm font-semibold text-ink">Log revenue</h2>}

      <div>
        <label className="label">Granularity</label>
        <SegmentedControl
          size="md"
          ariaLabel="Granularity"
          options={[
            { value: "daily", label: "Daily" },
            { value: "weekly", label: "Weekly" },
            { value: "monthly", label: "Monthly" },
          ]}
          value={periodType}
          onChange={setPeriodType}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {periodType === "monthly" ? (
          <div>
            <label className="label">Month</label>
            <input
              type="month"
              className="input"
              value={start.slice(0, 7)}
              onChange={(e) => {
                if (!e.target.value) return;
                const b = monthBounds(e.target.value);
                setStart(b.start);
                setEnd(b.end);
              }}
              required
            />
          </div>
        ) : (
          <>
            <div>
              <label className="label">{periodType === "daily" ? "Date" : "Week start"}</label>
              <input type="date" className="input" value={start} onChange={(e) => setStart(e.target.value)} required />
            </div>
            {periodType === "weekly" && (
              <div>
                <label className="label">Week end</label>
                <input type="date" className="input" value={end} min={start} onChange={(e) => setEnd(e.target.value)} required />
              </div>
            )}
          </>
        )}
      </div>
      {periodType === "monthly" && (
        <p className="-mt-2 text-xs text-muted">
          Covers {longDate(start)} – {longDate(end)}.
        </p>
      )}

      <div>
        <label className="label">Amount (Rs / MUR)</label>
        <input
          type="number"
          min={0}
          step="0.01"
          className="input tnum"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />
      </div>

      <div>
        <label className="label">Note (optional)</label>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      {err && <ErrorNote message={err} />}

      <button type="submit" className="btn-primary w-full" disabled={save.isPending}>
        {save.isPending && <Spinner className="h-4 w-4" />}
        {entry ? "Save changes" : "Add entry"}
      </button>
    </form>
  );
}
